import './PlanTitle.scss';

import { CaretDown, CaretUp } from '@phosphor-icons/react';
import {
  ButtonV2,
  OperatorLogo,
  ProductOffering,
  type Resource,
} from '@travelwin/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import CountryCoverageModal from '@/components/CountryCoverageModal';
import { Price } from '@/components/PlanCard/components/Price';
import { useCountryUtils } from '@/hooks/useCountryUtils';

import { PlanData } from '../PlanCard/components/PlanData';

const className = 'c-PlanTitle';

type PlanTitleProps = {
  planDetails: ProductOffering;
  expandable?: boolean;
  expanded?: boolean;
};

export const PlanCoverage = ({ countries }: { countries: Resource[] }) => {
  const [countryCoverageModal, setCountryCoverageModal] = useState(false);
  const { t } = useTranslation(['ecommerce']);

  if (countries.length > 1) {
    return (
      <>
        <ButtonV2
          asChild
          size={'small'}
          variant="link-no-underline"
          intent="success-green"
        >
          <span
            className={`${className}--clickable`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCountryCoverageModal(true);
            }}
          >
            {t('plan_card.countries_count', {
              count: countries.length,
              defaultValue: '{{count}} country',
              defaultValue_other: '{{count}} countries',
            })}
          </span>
        </ButtonV2>

        <CountryCoverageModal
          key="countries-coverage"
          on={countryCoverageModal}
          onCloseButton={() => setCountryCoverageModal(false)}
          countries={countries}
        />
      </>
    );
  }

  return null;
};

export const PlanTitle = ({
  planDetails,
  expandable,
  expanded,
}: PlanTitleProps) => {
  const { t } = useTranslation(['ecommerce']);
  const { getCountryNameTranslation } = useCountryUtils();

  const currencyAlias = planDetails.product_offering_pricing[0].currency.alias;
  const price = planDetails.product_offering_pricing[0].total_price_gross;

  return (
    <div className={className}>
      <div className={`${className}__title`}>
        <div className={`${className}__name`}>
          <div
            className={`${className}__plan-name`}
            data-testid="div-plan-name"
          >
            <PlanData data={planDetails.data} /> {t('plan_card.esim', 'eSIM')}
          </div>
          <div className={`${className}__country-name`}>
            {getCountryNameTranslation(planDetails.country.description)}
          </div>
          <PlanCoverage countries={planDetails.countries} />
          {planDetails.roaming ? (
            <div className={`${className}__roaming`}>
              {t('plan_coverage.roaming', '+Roaming')}
            </div>
          ) : null}
          {planDetails.free_text ? (
            <div className={`${className}__free-text`}>
              {planDetails.free_text}
            </div>
          ) : null}
        </div>
        <div className={`${className}__operator`}>
          <div className={`${className}__operator-icon`}>
            <OperatorLogo name={planDetails.icon_uri} />
          </div>
          <div className={`${className}__operator-name`}>
            {planDetails.mvno.description}
          </div>
        </div>
      </div>

      {expandable && (
        <div className={`${className}__expandable`}>
          {<Price currencyAlias={currencyAlias} price={price} />}
          {expanded ? (
            <CaretUp size={24} className={`${className}__caret`} />
          ) : (
            <CaretDown size={24} className={`${className}__caret`} />
          )}
        </div>
      )}
    </div>
  );
};
